import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { useFocusTrap } from '@/lib/hooks';
import {
  buildResults,
  pushRecent,
  readRecents,
  type PaletteResult,
} from '@/lib/palette';
import { ConfirmDialog } from './ConfirmDialog';

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Ctrl or Cmd K. One input, results grouped as you type, keyboard first.
 *
 * Recognition happens in lib/palette.ts so the rules can be tested without a DOM. A command
 * that touches production does not run from here: it opens its confirm dialog first, and in
 * practice that means it navigates to the control that owns the ceremony.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const id = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pending, setPending] = useState<PaletteResult | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const trapRef = useFocusTrap(open);

  const results = useMemo<PaletteResult[]>(() => {
    if (query.trim().length === 0) return readRecents();
    return buildResults(query);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open || typeof document === 'undefined') return null;

  const go = (result: PaletteResult) => {
    pushRecent(result);
    if (result.run) {
      result.run();
    } else if (result.to) {
      void navigate({
        to: result.to,
        ...(result.search ? { search: result.search } : {}),
      });
    }
    onClose();
  };

  const choose = (result: PaletteResult | undefined) => {
    if (!result) return;
    if (result.production) {
      setPending(result);
      return;
    }
    go(result);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(results.length - 1, current + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(0, current - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(results[active]);
    }
  };

  let lastGroup = '';

  return createPortal(
    <>
      <button type="button" className="o-scrim" aria-label="Close" tabIndex={-1} onClick={onClose} />
      <div
        className="o-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        ref={(node) => {
          trapRef.current = node;
        }}
      >
        <input
          ref={inputRef}
          className="o-input o-palette__input"
          role="combobox"
          aria-expanded="true"
          aria-controls={`${id}-list`}
          aria-activedescendant={results[active] ? `${id}-${results[active].id}` : undefined}
          aria-autocomplete="list"
          aria-label="Search orders, customers, SKUs, tracking numbers, or type > for a command"
          placeholder="Order number, ELU ref, name, SKU, tracking, or > command"
          value={query}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />

        <p className="o-visually-hidden" role="status" aria-live="polite">
          {results.length} results
        </p>

        <ul className="o-palette__list" id={`${id}-list`} role="listbox" aria-label="Results">
          {results.length === 0 ? (
            <li className="o-palette__group">No matches. Try an order number or a name.</li>
          ) : null}
          {results.map((result, index) => {
            const header = result.group !== lastGroup ? result.group : null;
            lastGroup = result.group;
            return (
              <li key={result.id}>
                {header ? <p className="o-palette__group">{header}</p> : null}
                <div
                  id={`${id}-${result.id}`}
                  role="option"
                  aria-selected={index === active}
                  className="o-palette__item"
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(result)}
                >
                  <span>{result.label}</span>
                  {result.detail ? <span className="o-palette__item-sub">{result.detail}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={pending?.label ?? ''}
        body="This command opens a control that changes the live storefront. The control itself asks for a step-up, a reason and a typed confirmation before anything changes."
        effects={['Opens Settings > Controls', 'Nothing changes until you confirm there']}
        confirmLabel="Open the control"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          const target = pending;
          setPending(null);
          if (target) go(target);
        }}
      />
    </>,
    document.body,
  );
}
